import type {
  AdminAudienceState,
  AudienceReply,
  ProjectorBoardSort,
  ProjectorState,
  RoomThread,
} from "../../shared/types.ts";
import { DEFAULT_PROJECTOR_BOARD_SORT } from "../../shared/threadSorting.ts";

const DEFAULT_BOARD_SORT: ProjectorBoardSort = DEFAULT_PROJECTOR_BOARD_SORT;
const DEFAULT_REPLIES_VISIBLE = true;

export type AudienceOptimisticAction =
  | { id: string; kind: "postStatus"; postId: string; status: RoomThread["status"]; now: number }
  | { id: string; kind: "editPost"; postId: string; body: string; now: number }
  | { id: string; kind: "replyStatus"; replyId: string; status: AudienceReply["status"]; now: number }
  | { id: string; kind: "projectThread"; threadId: string; showReplies: boolean; now: number }
  | { id: string; kind: "showDefault"; sessionId: string; now: number }
  | { id: string; kind: "showBoard"; sessionId: string; boardSort: ProjectorBoardSort; now: number }
  | { id: string; kind: "showJoinQr"; sessionId: string; now: number }
  | { id: string; kind: "showKeyThemes"; sessionId: string; now: number }
  | { id: string; kind: "showKeyThemesBoard"; sessionId: string; now: number }
  | { id: string; kind: "setRepliesVisible"; sessionId: string; visible: boolean; now: number };

export interface PendingAudienceIds {
  threadIds: ReadonlySet<string>;
  replyIds: ReadonlySet<string>;
  projector: boolean;
}

export function applyOptimisticAudienceActions(
  state: AdminAudienceState,
  actions: AudienceOptimisticAction[],
): AdminAudienceState {
  return actions.reduce((next, action) => applyOptimisticAudienceAction(next, action), state);
}

export function applyOptimisticAudienceAction(
  state: AdminAudienceState,
  action: AudienceOptimisticAction,
): AdminAudienceState {
  switch (action.kind) {
    case "postStatus":
      return updatePostStatus(state, action.postId, action.status, action.now);
    case "editPost":
      return updatePostBody(state, action.postId, action.body.trim(), action.now);
    case "replyStatus":
      return updateReplyStatus(state, action.replyId, action.status);
    case "projectThread":
      return {
        ...state,
        projector: replaceProjector(state, action.now, {
          mode: "thread",
          projectedThreadId: action.threadId,
          showReplies: action.showReplies,
          boardSort: state.projector?.boardSort ?? DEFAULT_BOARD_SORT,
        }),
      };
    case "showDefault":
      return {
        ...state,
        projector: replaceProjector(state, action.now, {
          mode: "board",
          showReplies: DEFAULT_REPLIES_VISIBLE,
          boardSort: DEFAULT_BOARD_SORT,
        }),
      };
    case "showBoard":
      return {
        ...state,
        projector: replaceProjector(state, action.now, {
          mode: "board",
          showReplies: DEFAULT_REPLIES_VISIBLE,
          boardSort: action.boardSort,
        }),
      };
    case "showJoinQr":
      return {
        ...state,
        projector: replaceProjector(state, action.now, {
          mode: "join-qr",
          showReplies: DEFAULT_REPLIES_VISIBLE,
          boardSort: state.projector?.boardSort ?? DEFAULT_BOARD_SORT,
        }),
      };
    case "showKeyThemes":
      return {
        ...state,
        projector: replaceProjector(state, action.now, {
          mode: "key-themes",
          showReplies: DEFAULT_REPLIES_VISIBLE,
          boardSort: state.projector?.boardSort ?? DEFAULT_BOARD_SORT,
        }),
      };
    case "showKeyThemesBoard":
      return {
        ...state,
        projector: replaceProjector(state, action.now, {
          mode: "key-themes-board",
          showReplies: DEFAULT_REPLIES_VISIBLE,
          boardSort: state.projector?.boardSort ?? DEFAULT_BOARD_SORT,
        }),
      };
    case "setRepliesVisible":
      return {
        ...state,
        projector: patchProjector(state, action.now, { showReplies: action.visible }),
      };
  }
}

export function isOptimisticActionSatisfied(
  state: AdminAudienceState,
  action: AudienceOptimisticAction,
): boolean {
  switch (action.kind) {
    case "postStatus": {
      const thread = state.threads.find((entry) => entry.id === action.postId);
      if (!thread) return action.status === "hidden" || action.status === "archived";
      return thread.status === action.status;
    }
    case "editPost": {
      const thread = state.threads.find((entry) => entry.id === action.postId);
      return thread?.body === action.body.trim();
    }
    case "replyStatus": {
      const reply = findReply(state, action.replyId);
      if (!reply) return action.status === "hidden" || action.status === "archived";
      return reply.status === action.status;
    }
    case "projectThread":
      return state.projector?.mode === "thread"
        && state.projector.projectedThreadId === action.threadId
        && state.projector.showReplies === action.showReplies;
    case "showDefault":
      return state.projector?.mode === "board"
        && state.projector.boardSort === DEFAULT_BOARD_SORT
        && state.projector.projectedThreadId === undefined
        && state.projector.projectedArtifactId === undefined;
    case "showBoard":
      return state.projector?.mode === "board"
        && state.projector.boardSort === action.boardSort
        && state.projector.projectedThreadId === undefined
        && state.projector.projectedArtifactId === undefined;
    case "showJoinQr":
      return state.projector?.mode === "join-qr"
        && state.projector.projectedThreadId === undefined
        && state.projector.projectedArtifactId === undefined;
    case "showKeyThemes":
      return state.projector?.mode === "key-themes"
        && state.projector.projectedThreadId === undefined
        && state.projector.projectedArtifactId === undefined;
    case "showKeyThemesBoard":
      return state.projector?.mode === "key-themes-board"
        && state.projector.projectedThreadId === undefined
        && state.projector.projectedArtifactId === undefined;
    case "setRepliesVisible":
      return state.projector?.showReplies === action.visible;
  }
}

export function collectPendingAudienceIds(actions: AudienceOptimisticAction[]): PendingAudienceIds {
  const threadIds = new Set<string>();
  const replyIds = new Set<string>();
  let projector = false;

  for (const action of actions) {
    if (action.kind === "postStatus" || action.kind === "editPost") threadIds.add(action.postId);
    if (action.kind === "replyStatus") replyIds.add(action.replyId);
    if (
      action.kind === "projectThread"
      || action.kind === "showDefault"
      || action.kind === "showBoard"
      || action.kind === "showJoinQr"
      || action.kind === "showKeyThemes"
      || action.kind === "showKeyThemesBoard"
      || action.kind === "setRepliesVisible"
    ) {
      projector = true;
    }
  }

  return { threadIds, replyIds, projector };
}

function updatePostStatus(
  state: AdminAudienceState,
  postId: string,
  status: RoomThread["status"],
  now: number,
): AdminAudienceState {
  const visible = status === "shown" || status === "answered";
  const threads = state.threads.map((thread) =>
    thread.id !== postId
      ? thread
      : {
          ...thread,
          status,
          lastActivityAt: visible ? Math.max(thread.lastActivityAt, now) : thread.lastActivityAt,
        },
  );

  const projector = state.projector?.projectedThreadId === postId && status === "hidden"
    ? replaceProjector(state, now, {
        mode: "board",
        showReplies: DEFAULT_REPLIES_VISIBLE,
        boardSort: state.projector?.boardSort ?? DEFAULT_BOARD_SORT,
      })
    : state.projector;

  return { ...state, threads, projector };
}

function updatePostBody(
  state: AdminAudienceState,
  postId: string,
  body: string,
  now: number,
): AdminAudienceState {
  if (!body) return state;
  return {
    ...state,
    threads: state.threads.map((thread) =>
      thread.id !== postId
        ? thread
        : { ...thread, body, lastActivityAt: Math.max(thread.lastActivityAt, now) },
    ),
  };
}

function updateReplyStatus(
  state: AdminAudienceState,
  replyId: string,
  status: AudienceReply["status"],
): AdminAudienceState {
  const threads = state.threads.map((thread) => {
    const previousReply = (thread.replies ?? []).find((reply) => reply.id === replyId);
    if (!previousReply) return thread;

    const replies = (thread.replies ?? []).map((reply) => {
      if (reply.id !== replyId) return reply;
      return { ...reply, status };
    });

    const wasVisible = previousReply.status === "shown";
    const willBeVisible = status === "shown";
    const replyCount = wasVisible === willBeVisible
      ? thread.replyCount
      : willBeVisible
        ? thread.replyCount + 1
        : Math.max(0, thread.replyCount - 1);

    return {
      ...thread,
      replies,
      replyCount,
      lastActivityAt: willBeVisible
        ? Math.max(thread.lastActivityAt, previousReply.createdAt)
        : thread.lastActivityAt,
    };
  });

  return { ...state, threads };
}

function replaceProjector(
  state: Pick<AdminAudienceState, "sessionId">,
  now: number,
  next: Pick<ProjectorState, "mode" | "showReplies" | "boardSort">
    & Partial<Pick<ProjectorState, "projectedThreadId" | "projectedArtifactId" | "highlightReplyId" | "boardCategoryIds">>,
): ProjectorState {
  return {
    sessionId: state.sessionId,
    mode: next.mode,
    projectedThreadId: next.projectedThreadId,
    projectedArtifactId: next.projectedArtifactId,
    highlightReplyId: next.highlightReplyId,
    showReplies: next.showReplies,
    boardCategoryIds: next.boardCategoryIds,
    boardSort: next.boardSort,
    updatedAt: now,
    updatedBy: "operator",
  };
}

function patchProjector(
  state: Pick<AdminAudienceState, "sessionId" | "projector">,
  now: number,
  patch: Partial<Pick<ProjectorState, "showReplies">>,
): ProjectorState {
  return {
    sessionId: state.sessionId,
    mode: state.projector?.mode ?? "board",
    projectedThreadId: state.projector?.projectedThreadId,
    projectedArtifactId: state.projector?.projectedArtifactId,
    highlightReplyId: state.projector?.highlightReplyId,
    showReplies: patch.showReplies ?? state.projector?.showReplies ?? DEFAULT_REPLIES_VISIBLE,
    boardCategoryIds: state.projector?.boardCategoryIds,
    boardSort: state.projector?.boardSort ?? DEFAULT_BOARD_SORT,
    updatedAt: now,
    updatedBy: "operator",
  };
}

function findReply(state: AdminAudienceState, replyId: string) {
  for (const thread of state.threads) {
    const reply = thread.replies?.find((entry) => entry.id === replyId);
    if (reply) return reply;
  }
  return null;
}
